import React, { useEffect, useRef, useState } from 'react';
import { Check, Globe, Link2, Loader2, Lock, X } from 'lucide-react';
import type { MeetingShareAccess, MeetingShareDTO, ShareApi } from '../../global';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { formatClock, formatDate } from '../../components/ui/styles';
import { t } from '../../lib/i18n';

interface ShareMeetingDialogProps {
  open: boolean;
  /** Toplantı ya da klasör paylaşımının uçları. */
  api: ShareApi;
  targetId: string;
  title: string;
  kind?: 'meeting' | 'folder';
  onClose: () => void;
}

const EMAIL = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/;
const splitEmails = (value: string) => value.split(/[\s,;]+/).map((part) => part.trim().toLowerCase()).filter(Boolean);

/**
 * Google Drive'daki gibi paylaşım penceresi: üstte kişi ekleme (davet e-postası gider),
 * ortada erişimi olanlar, altta genel erişim. Kısıtlıyken bağlantıyı yalnızca davetliler
 * e-postalarına gelen kodla açar; "herkes" seçilince bağlantıyı kopyalayıp göndermek yeter.
 */
export const ShareMeetingDialog: React.FC<ShareMeetingDialogProps> = ({ open, api, targetId, title, kind = 'meeting', onClose }) => {
  const [state, setState] = useState<MeetingShareDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [input, setInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Dialog açılırken odağı panele alıyor; yükleme bitince kutuya taşı, hemen yazılabilsin.
  useEffect(() => {
    if (open && !loading) inputRef.current?.focus();
  }, [open, loading]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setInput('');
    api
      .get(targetId)
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || t('Paylaşım bilgisi okunamadı.', 'Could not load sharing settings.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, api, targetId]);

  const run = async (action: () => Promise<MeetingShareDTO | undefined>) => {
    setSaving(true);
    setError(null);
    try {
      const next = await action();
      if (next) setState(next);
      return next;
    } catch (err) {
      setError((err as Error).message || t('İşlem yapılamadı, tekrar dene.', 'That didn’t work, try again.'));
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    const emails = splitEmails(input);
    if (emails.length === 0 || busy || !state) return;
    const invalid = emails.find((email) => !EMAIL.test(email));
    if (invalid) {
      setError(t(`Geçersiz e-posta adresi: ${invalid}`, `Invalid email address: ${invalid}`));
      return;
    }
    const next = await run(() => api.addRecipients(targetId, emails));
    if (!next) return;
    const failed = next.failed ?? [];
    setInput(failed.join(', '));
    if (failed.length > 0) setError(t('Bu adreslere davet gönderilemedi, tekrar dene.', 'The invitation couldn’t be sent to these addresses, try again.'));
  };

  const copyLink = () => {
    if (!state) return;
    navigator.clipboard.writeText(state.link).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => setError(t('Bağlantı kopyalanamadı.', 'Could not copy the link.'))
    );
  };

  const access: MeetingShareAccess = state?.access ?? 'restricted';
  const isPublic = access === 'public';
  const busy = loading || saving;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(`“${title}” paylaş`, `Share “${title}”`)}
      className="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={copyLink} disabled={!state} className="sm:mr-auto">
            {copied ? <Check className="size-4 text-emerald-500" /> : <Link2 className="size-4" />}
            {copied ? t('Kopyalandı', 'Copied') : t('Bağlantıyı kopyala', 'Copy link')}
          </Button>
          <Button onClick={onClose}>{t('Bitti', 'Done')}</Button>
        </>
      }
    >
      <div className="space-y-5 text-sm">
        <form onSubmit={(event) => void invite(event)} className="flex gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode="email"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={t('Kişi ekle: e-posta adresi', 'Add people by email')}
            aria-label={t('Kişi ekle', 'Add people')}
          />
          <Button type="submit" disabled={busy || !state || input.trim() === ''}>
            {saving && input.trim() !== '' ? <Loader2 className="size-4 animate-spin" /> : null}
            {t('Davet et', 'Invite')}
          </Button>
        </form>

        {error && <p className="text-rose-500">{error}</p>}

        <div>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('Erişimi olan kişiler', 'People with access')}</div>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('Yükleniyor…', 'Loading…')}
            </div>
          ) : state && state.recipients.length > 0 ? (
            <ul className="space-y-1">
              {state.recipients.map((recipient) => (
                <li key={recipient.id} className="flex items-center gap-2 rounded-md py-1">
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{recipient.email}</div>
                    <div className="text-xs text-muted-foreground">
                      {recipient.lastViewedAt
                        ? t(`Son görüntüleme: ${formatDate(recipient.lastViewedAt)} ${formatClock(recipient.lastViewedAt)}`, `Last viewed ${formatDate(recipient.lastViewedAt)} ${formatClock(recipient.lastViewedAt)}`)
                        : t('Davet edildi', 'Invited')}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={busy}
                    aria-label={t(`${recipient.email} adresini çıkar`, `Remove ${recipient.email}`)}
                    title={t('Erişimi kaldır', 'Remove access')}
                    onClick={() => void run(() => api.removeRecipient(targetId, recipient.id))}
                  >
                    <X className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : state ? (
            <p className="text-muted-foreground">{t('Henüz kimseyi eklemedin.', 'You haven’t added anyone yet.')}</p>
          ) : null}
        </div>

        <div>
          <div id="share-access-label" className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('Genel erişim', 'General access')}
          </div>
          <div className="flex items-start gap-3">
            <span className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${isPublic ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300' : 'bg-accent text-muted-foreground'}`}>
              {isPublic ? <Globe className="size-4" /> : <Lock className="size-4" />}
            </span>
            <div className="min-w-0 flex-1 space-y-1">
              <Select<MeetingShareAccess>
                aria-labelledby="share-access-label"
                value={access}
                disabled={busy || !state}
                onChange={(next) => void run(() => api.setAccess(targetId, next))}
                options={[
                  { value: 'restricted', label: t('Kısıtlı', 'Restricted') },
                  { value: 'public', label: t('Bağlantıya sahip herkes', 'Anyone with the link') },
                ]}
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                {isPublic
                  ? t('Bağlantıyı alan herkes giriş yapmadan görebilir.', 'Anyone who gets the link can see it without signing in.')
                  : t('Bağlantıyı yalnızca eklediğin kişiler, e-postalarına gelen kodla açabilir.', 'Only people you add can open the link, with a code sent to their email.')}
              </p>
            </div>
          </div>
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          {kind === 'folder'
            ? t(
                'Paylaşılan: klasördeki toplantıların özeti, kararları, aksiyon maddeleri ve transkripti. Klasöre sonradan eklediğin toplantılar da görünür. Özel notların ve ses kaydı paylaşılmaz.',
                'Shared: the summary, decisions, action items and transcript of the meetings in this folder, including ones you add later. Your private notes and the recording aren’t shared.'
              )
            : t('Paylaşılan: özet, kararlar, aksiyon maddeleri, transkript. Özel notların ve ses kaydı paylaşılmaz.', 'Shared: summary, decisions, action items, transcript. Your private notes and the recording aren’t shared.')}
        </p>
      </div>
    </Dialog>
  );
};
